/**
 * Copyright 2025-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { Dispatch } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import dayjs from "dayjs";
import styled from "styled-components";
import { FlexContainer, Tag, theme } from "@nordcloud/gnui";
import { Report } from "~/generated/graphql";
import {
  LargeColumn,
  MediumColumn,
  SmallColumn,
  SmallIconColumn,
} from "~/components/Tables/styles";
import { dateFormat, TABLE_LABELS } from "~/constants";
import { Actions } from "./Actions";
import { ReportStatus } from "./ReportStatus";
import { getReportTypeLabel } from "./reportTypeLabels";

const columnHelper = createColumnHelper<Report>();

const TextWrap = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
  min-width: 0;
`;

type Props = {
  highlightedReportId?: string;
  setHighlightedReportId?: Dispatch<string | undefined>;
};

export function columns({
  highlightedReportId,
  setHighlightedReportId,
}: Props) {
  return [
    columnHelper.accessor(() => null, {
      id: "status",
      header: () => null,
      cell: ({ row }) => {
        return <ReportStatus status={row.original.status} />;
      },
      meta: SmallIconColumn,
    }),
    columnHelper.accessor(() => null, {
      id: "name",
      header: () => "Report Name",
      cell: ({ row }) => {
        if (highlightedReportId && row.original.id === highlightedReportId) {
          return (
            <FlexContainer>
              <TextWrap>{row.original.name}</TextWrap>
              <Tag color={theme.colors.notification} text={"New"} />
            </FlexContainer>
          );
        }
        return <TextWrap>{row.original.name}</TextWrap>;
      },
      meta: LargeColumn,
    }),
    columnHelper.accessor(() => null, {
      id: "type",
      header: () => "Report Type",
      cell: ({ row }) => {
        const displayType = getReportTypeLabel(row.original.type);
        return <Tag text={displayType} />;
      },
      meta: MediumColumn,
    }),
    columnHelper.accessor(() => null, {
      id: "format",
      header: () => "Format",
      cell: () => {
        return <Tag text="CSV" />;
      },
      meta: SmallColumn,
    }),
    columnHelper.accessor(() => null, {
      id: "dateGenerated",
      header: () => "Date Generated",
      cell: ({ row }) => {
        return (
          <FlexContainer>
            {row.original.dateGenerated
              ? dayjs(row.original.dateGenerated).format(
                  dateFormat.dayMonthYearHourMinute
                )
              : ""}
          </FlexContainer>
        );
      },
      meta: MediumColumn,
    }),
    columnHelper.accessor(() => null, {
      id: "createdBy",
      header: () => TABLE_LABELS.createdBy,
      cell: ({ row }) => {
        return <TextWrap>{row.original.changeDetails.createdBy}</TextWrap>;
      },
      meta: LargeColumn,
    }),
    columnHelper.accessor(() => null, {
      id: "options",
      header: () => null,
      cell: ({ row }) => {
        const reportDetails = {
          id: row.original?.id,
          status: row.original.status,
          url: row.original?.url,
          name: row.original.name,
        };
        return (
          <Actions
            {...reportDetails}
            setHighlightedReportId={
              row.original?.id === highlightedReportId
                ? setHighlightedReportId
                : undefined
            }
          />
        );
      },
      meta: SmallIconColumn,
    }),
  ] as never;
}
